import cn from 'classnames';
import { isEmpty } from 'lodash-es';
import { FC, memo } from 'react';
import { useIntl } from 'react-intl';

import { Decision } from '@/shared/api/protocol-ts/model/dto_report_common_pb';
import { Checkbox, Icon, Radio, Tooltip } from '@/shared/ui';

import { conditionText } from '../../config';
import {
  CompactCondition,
  ConditionColor,
  ConditionDecisionChange,
} from '../../config/types';

import styles from './ConditionToggle.module.scss';

const colorStyle: Record<ConditionColor, string> = {
  yellow: styles.colorYellow,
  red: styles.colorRed,
  purple: styles.colorPurple,
  white: styles.colorWhite,
};

type ConditionToggleProps = {
  className?: string;
  data: CompactCondition;
  disabled?: boolean;
  onChange: ConditionDecisionChange;
};

type ConditionToggleMarkerProps = {
  marker: CompactCondition['marker'];
  probabilityText: CompactCondition['probabilityText'];
};

const ConditionToggleMarker: FC<ConditionToggleMarkerProps> = (props) => {
  const { marker, probabilityText } = props;

  const { formatMessage } = useIntl();

  if (marker === 'manual' || marker === 'aiNotDetected') {
    const tooltipText =
      marker === 'manual'
        ? formatMessage({
            id: 'condition.marker.manual',
            defaultMessage: 'Marked by the doctor',
          })
        : formatMessage({
            id: 'condition.marker.aiNotDetected',
            defaultMessage: 'Not detected by AI yet — add manually',
          });

    return (
      <Tooltip.Primary content={tooltipText}>
        <span
          className={cn(
            styles.marker,
            styles.markerNeutral,
            styles.markerManual,
          )}
        >
          <Icon name="user-bold" size={12} className={styles.markerIcon} />
        </span>
      </Tooltip.Primary>
    );
  }

  if (marker === 'aiFound' || marker === 'aiLowProbability') {
    return (
      <span
        className={cn(
          styles.marker,
          marker === 'aiFound' ? styles.markerFound : styles.markerNeutral,
        )}
      >
        <Icon
          name="cat"
          size={16}
          className={cn(marker === 'aiLowProbability' && styles.markerIcon)}
        />
        <span className={styles.markerText}>{probabilityText}</span>
      </span>
    );
  }

  return null;
};

const InnerConditionToggle: FC<ConditionToggleProps> = (props) => {
  const { className, data, disabled, onChange } = props;

  const { formatMessage } = useIntl();

  if (isEmpty(data)) {
    return null;
  }

  const name = data.isOneOf ? data.group : data.code.toString();
  const Component = data.isOneOf ? Radio : Checkbox;

  const handleChange = () => {
    onChange({
      conditionIDs: data.ids,
      parentConditionId: data.parentID,
      parentConditionCode: data.parentCode,
      conditionCode: data.code,
      userDecision: data.isChecked
        ? Decision.NegativeDecision
        : Decision.PositiveDecision,
    });
  };

  return (
    <Component
      id={data?.id}
      className={cn(
        styles.container,
        { [styles.disabled]: disabled },
        className,
      )}
      checked={data.isChecked ?? false}
      disabled={disabled}
      onChange={handleChange}
      name={name}
    >
      <div className={styles.label}>
        <span className={cn(styles.text, colorStyle[data.color])}>
          {data.code in conditionText &&
            formatMessage(conditionText[data.code])}
        </span>
        <ConditionToggleMarker
          marker={data.marker}
          probabilityText={data.probabilityText}
        />
      </div>
    </Component>
  );
};

export const ConditionToggle = memo(InnerConditionToggle);
